/* eslint-disable @typescript-eslint/no-explicit-any */
import { ALL_SELECT_VALUE_STRING } from '../../../utils/constants/global-constants'
import { mpfRListsHttp } from '../api/mpf-r-lists-http'
import { formatStringForServer } from '../formatting/formatting'
import { and } from './data'

interface UserProfile {
  AssignedComponentCode: number
  DoDNumber: string
  DutyEmail: string
  DutyPhone: string
  Installation: string
  ItemCreated: Date
  ItemCreatedBy: any //Person_Single
  ItemModified: Date
  ItemModifiedBy: any //Person_Single
  OrgMAJCOM: string
  Organization: string
  PASCODE: string
  PASCODEServicedBy: string
  PersonalEmail: string
  PersonalPhone: string
  PersonalPhoneProvider: string
  ProfileStatus: string
  Rank: string
  ServicingMAJCOM: string
  ServicingOrganization: string
  UserDisplayName: string
  UserRID: string
  eKey: string
  eTag: string
}

interface UserProfileArgs {
  isListModified?: boolean
  onePageOnly?: boolean
  pageNumber?: number
  searchFilter?: string
  userRID?: string
}
interface ProfileCount {
  eKey_count: number
}
const COLUMN_NAMES = {
  AssignedComponentCode: 'AssignedComponentCode',
  DoDNumber: 'DoDNumber',
  DutyEmail: 'DutyEmail',
  DutyPhone: 'DutyPhone',
  EKey: 'eKey',
  ETag: 'eTag',
  Installation: 'Installation',
  ItemCreated: 'ItemCreated',
  ItemCreatedBy: 'ItemCreatedBy',
  ItemModified: 'ItemModified',
  ItemModifiedBy: 'ItemModifiedBy',
  OrgMAJCOM: 'OrgMAJCOM',
  Organization: 'Organization',
  PASCODEServicedBy: 'PASCODEServicedBy',
  PascodeID: 'PascodeID',
  PersonalEmail: 'PersonalEmail',
  PersonalPhone: 'PersonalPhone',
  PersonalPhoneProvider: 'PersonalPhoneProvider',
  ProfileStatus: 'ProfileStatus',
  Rank: 'Rank',
  ServicingMAJCOM: 'ServicingMAJCOM',
  ServicingOrganization: 'ServicingOrganization',
  UserDisplayName: 'UserDisplayName',
  UserRID: 'UserRID',
}
const constructInquiryFilter = (args: UserProfileArgs): string => {
  const filters: string[] = []
  if (args?.searchFilter && args.searchFilter !== ALL_SELECT_VALUE_STRING) {
    //This will need to change in the future from startswith to contains, however currently contains is yet to be implemented.
    filters.push(
      `(contains(DoDNumber, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}') or contains(DutyEmail, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}'))`
    )
  }
  if (args?.userRID) {
    filters.push(`UserRID eq '${args.userRID}'`)
  }
  const filter = and(...filters)
  return filter
}
const getProfileCount = async (
  args: UserProfileArgs
): Promise<ProfileCount[]> => {
  const list = await mpfRListsHttp().getList(LIST_NAME)
  const filter = constructInquiryFilter(args)
  const items = await list
    .query()
    .filter(filter)
    .select(COLUMN_NAMES.EKey)
    .count(COLUMN_NAMES.EKey)
    .get()

  return [...items] as ProfileCount[]
}

const getUserProfile = async (
  args: UserProfileArgs
): Promise<UserProfile[]> => {
  try {
    const pageNum = args?.pageNumber ?? -1
    const PAGE_SIZE = 100
    const onePageOnly = args?.onePageOnly ?? false
    const list = await mpfRListsHttp().getList(LIST_NAME)
    const filter = constructInquiryFilter(args)
    const query = await list
      .query()
      .filter(filter)
      .select(
        COLUMN_NAMES.AssignedComponentCode,
        COLUMN_NAMES.DoDNumber,
        COLUMN_NAMES.DutyEmail,
        COLUMN_NAMES.DutyPhone,
        COLUMN_NAMES.PascodeID,
        COLUMN_NAMES.PersonalEmail,
        COLUMN_NAMES.PersonalPhone,
        COLUMN_NAMES.PersonalPhoneProvider,
        COLUMN_NAMES.ProfileStatus,
        COLUMN_NAMES.Rank,
        COLUMN_NAMES.UserDisplayName,
        COLUMN_NAMES.UserRID,
        COLUMN_NAMES.ETag,
        COLUMN_NAMES.EKey,
        'PASCODEMetadata/PASCODE',
        'PASCODEMetadata/Installation',
        'PASCODEMetadata/Organization',
        'PASCODEMetadata/ServicingBy',
        'PASCODEMetadata/OrgMAJCOM',
        'PASCODEMetadata/ServicingMAJCOM',
        'PASCODEMetadata/ServicingOrganization',
        'AssignedComponent/MetadataValue'
      )
      .expand('PASCODEMetadata', 'AssignedComponent')
      .orderBy([{ ascending: false, orderBy: 'PascodeID' }])

    const startAtPageQuery =
      pageNum > -1 ? query.skip(pageNum * PAGE_SIZE) : query

    const oneOrManyPagesQuery = onePageOnly
      ? startAtPageQuery.top(PAGE_SIZE)
      : startAtPageQuery

      const items: any = await oneOrManyPagesQuery.get()

    const formattedItems = items.map((item) => ({
      ...item,
      Installation: item['PASCODEMetadata/Installation'],
      OrgMAJCOM: item['PASCODEMetadata/OrgMAJCOM'],
      Organization: item['PASCODEMetadata/Organization'],
      PASCODE: item['PASCODEMetadata/PASCODE'],
      PASCODEServicedBy: item['PASCODEMetadata/ServicingBy'],
      ServicingMAJCOM: item['PASCODEMetadata/ServicingMAJCOM'],
      ServicingOrganization: item['PASCODEMetadata/ServicingOrganization'],
    }))

    return [...formattedItems.sort((a,b) => b.ItemModified - a.ItemModified)] as UserProfile[]
  } catch (error) {
    console.error(error)
    return [] as UserProfile[]
  }
}

const LIST_NAME = 'UserProfileList'
export {
  COLUMN_NAMES,
  LIST_NAME,
  type ProfileCount,
  type UserProfile,
  type UserProfileArgs,
  getProfileCount,
  getUserProfile,
}
